import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import VoiceCallAgent from './VoiceCallAgent';

export const REQUESTER_VOICE_CONFIG = {
  sessionKey: 'requester_voice_session_id',
  useFaq: false,
  useBackendAgent: true,
};

export default function VoiceCallWidget({
  displayName = '',
  prefilledEmail = '',
  voiceProps = {},
  fabBottom = 28,
  fabRight = 28,
  showFab = true,
  open: controlledOpen,
  onOpenChange,
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [inCall, setInCall] = useState(false);
  const [autoStart, setAutoStart] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;

  const setOpen = (value) => {
    if (onOpenChange) onOpenChange(value);
    else setInternalOpen(value);
  };

  const merged = { ...REQUESTER_VOICE_CONFIG, ...voiceProps };

  const handleFab = () => {
    if (inCall) return;
    if (!open) {
      setAutoStart(false);
      setOpen(true);
      return;
    }
    setOpen(false);
    setAutoStart(false);
  };

  const widget = (
    <>
      {showFab ? (
      <motion.button
        type="button"
        onClick={handleFab}
        whileHover={{ scale: inCall ? 1 : 1.08 }}
        whileTap={{ scale: inCall ? 1 : 0.94 }}
        title={inCall ? 'Voice call in progress' : 'Call AI Support (voice)'}
        disabled={inCall}
        style={{
          position: 'fixed',
          bottom: fabBottom,
          right: fabRight,
          zIndex: 1001,
          width: 52,
          height: 52,
          borderRadius: '50%',
          border: 'none',
          cursor: inCall ? 'default' : 'pointer',
          background: open || inCall
            ? (inCall ? '#16a34a' : '#EDF2F7')
            : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
          boxShadow: inCall
            ? '0 0 0 4px rgba(34,197,94,0.35), 0 6px 28px rgba(34,197,94,0.5)'
            : open
              ? '0 2px 12px rgba(0,0,0,0.12)'
              : '0 6px 24px rgba(34,197,94,0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background 0.2s, box-shadow 0.2s',
        }}
      >
        <AnimatePresence mode="wait" initial={false}>
          {open && !inCall ? (
            <motion.svg
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.18 }}
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#1A202C"
              strokeWidth="2.5"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </motion.svg>
          ) : (
            <motion.span
              key="phone"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.18 }}
              style={{ fontSize: 24, color: open && !inCall ? '#1A202C' : '#fff' }}
            >
              {inCall ? '📞' : '📞'}
            </motion.span>
          )}
        </AnimatePresence>

        {!open && !inCall && (
          <motion.div
            animate={{ scale: [1, 1.5], opacity: [0.45, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut' }}
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              background: '#22c55e',
              zIndex: -1,
            }}
          />
        )}
      </motion.button>
      ) : null}

      <AnimatePresence>
        {open && (
          <motion.div
            key="voice-panel"
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            style={{
              position: 'fixed',
              bottom: fabBottom + 64,
              right: fabRight,
              zIndex: 1000,
              width: 360,
              height: 420,
              maxWidth: 'calc(100vw - 32px)',
              maxHeight: 'min(480px, calc(100vh - 120px))',
              borderRadius: 20,
              overflow: 'hidden',
              boxShadow: '0 24px 64px rgba(0,0,0,0.22)',
              border: '1px solid #334155',
            }}
          >
            <VoiceCallAgent
              displayName={displayName}
              prefilledEmail={prefilledEmail}
              onCallStart={() => setInCall(true)}
              autoStart={autoStart}
              onEndCall={() => {
                setInCall(false);
                setAutoStart(false);
              }}
              {...merged}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );

  if (typeof document === 'undefined') return null;
  return ReactDOM.createPortal(widget, document.body);
}
